import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon,
  CubeIcon,
  CogIcon,
  TagIcon,
  WrenchIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';
import { api } from '../../services/api';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import { useForm } from 'react-hook-form';

interface ItemType {
  id: string;
  code: string;
  name: string;
  description?: string;
  nature: string;
  isInventoryTracked: boolean;
  isStockValued: boolean;
  isBatchTracked: boolean;
  isSerialTracked: boolean;
  hasExpiryDate: boolean;
  isMrpApplicable: boolean;
  isPhantomItem: boolean;
  costingMethod: string;
  allowNegativeStock: boolean;
  defaultSettings?: Record<string, any>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const ItemTypeCode = {
  RAW_MATERIAL: 'RM',
  SEMI_FINISHED: 'SFG',
  FINISHED_GOODS: 'FG',
  NON_INVENTORY: 'NON-INV',
  CONSUMABLE: 'CONS',
  SPARE_PARTS: 'SPARE',
  PACKAGING: 'PACK',
  TRADING: 'TRADING',
};

const ItemNature = {
  MANUFACTURED: 'manufactured',
  PURCHASED: 'purchased',
  BOTH: 'both',
  SERVICE: 'service',
};

const CostingMethod = {
  STANDARD: 'standard',
  AVERAGE: 'average',
  FIFO: 'fifo',
  LIFO: 'lifo',
};

const ItemTypeManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterNature, setFilterNature] = useState('');
  const [filterCode, setFilterCode] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'grouped'>('grid');
  
  const queryClient = useQueryClient();

  // Fetch item types
  const { data: itemTypesData, isLoading } = useQuery({
    queryKey: ['itemTypes', searchTerm, filterNature, filterCode],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filterNature) params.append('nature', filterNature);
      if (filterCode) params.append('code', filterCode);
      
      const response = await api.get(`/master-data/item-types?${params}`);
      return response.data;
    },
  });

  // Fetch grouped item types
  const { data: groupedData } = useQuery({
    queryKey: ['itemTypesGrouped'],
    queryFn: async () => {
      const response = await api.get('/master-data/item-types/grouped');
      return response.data;
    },
    enabled: viewMode === 'grouped',
  });

  // Create/Update item type
  const createUpdateMutation = useMutation({
    mutationFn: async (data: any) => {
      if (selectedItemType) {
        return api.patch(`/master-data/item-types/${selectedItemType.id}`, data);
      }
      return api.post('/master-data/item-types', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itemTypes'] });
      queryClient.invalidateQueries({ queryKey: ['itemTypesGrouped'] });
      toast.success(selectedItemType ? 'Item type updated successfully' : 'Item type created successfully');
      closeModal();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Operation failed');
    },
  });

  // Delete item type
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/master-data/item-types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itemTypes'] });
      queryClient.invalidateQueries({ queryKey: ['itemTypesGrouped'] });
      toast.success('Item type deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Delete failed');
    },
  });

  // Toggle status
  const toggleStatusMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.patch(`/master-data/item-types/${id}/toggle-status`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['itemTypes'] });
      queryClient.invalidateQueries({ queryKey: ['itemTypesGrouped'] });
      toast.success('Status updated successfully');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Status update failed');
    },
  });

  const openModal = (itemType?: ItemType) => {
    setSelectedItemType(itemType || null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedItemType(null);
  };

  const getCodeIcon = (code: string) => {
    switch (code) {
      case ItemTypeCode.RAW_MATERIAL:
        return <CubeIcon className="h-5 w-5" />;
      case ItemTypeCode.FINISHED_GOODS:
        return <ShoppingBagIcon className="h-5 w-5" />;
      case ItemTypeCode.SEMI_FINISHED:
        return <CogIcon className="h-5 w-5" />;
      case ItemTypeCode.SPARE_PARTS:
        return <WrenchIcon className="h-5 w-5" />;
      default:
        return <TagIcon className="h-5 w-5" />;
    }
  };

  const getCodeColor = (code: string) => {
    const codeColors = {
      [ItemTypeCode.RAW_MATERIAL]: 'bg-blue-100 text-blue-800',
      [ItemTypeCode.SEMI_FINISHED]: 'bg-yellow-100 text-yellow-800',
      [ItemTypeCode.FINISHED_GOODS]: 'bg-green-100 text-green-800',
      [ItemTypeCode.NON_INVENTORY]: 'bg-gray-100 text-gray-800',
      [ItemTypeCode.CONSUMABLE]: 'bg-purple-100 text-purple-800',
      [ItemTypeCode.SPARE_PARTS]: 'bg-red-100 text-red-800',
      [ItemTypeCode.PACKAGING]: 'bg-orange-100 text-orange-800',
      [ItemTypeCode.TRADING]: 'bg-indigo-100 text-indigo-800',
    };

    return codeColors[code] || 'bg-gray-100 text-gray-800';
  };

  const getNatureColor = (nature: string) => {
    const natureColors = {
      [ItemNature.MANUFACTURED]: 'bg-blue-50 text-blue-700',
      [ItemNature.PURCHASED]: 'bg-green-50 text-green-700',
      [ItemNature.BOTH]: 'bg-purple-50 text-purple-700',
      [ItemNature.SERVICE]: 'bg-orange-50 text-orange-700',
    };

    return natureColors[nature] || 'bg-gray-50 text-gray-700';
  };

  const renderItemTypeCard = (itemType: ItemType) => (
    <Card key={itemType.id} className="hover:shadow-lg transition-shadow">
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            {getCodeIcon(itemType.code)}
            <div>
              <h3 className="font-semibold text-gray-900">{itemType.name}</h3>
              <p className="text-sm text-gray-500">{itemType.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getCodeColor(itemType.code)}`}>
              {itemType.code}
            </span>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Nature:</span>
            <span className={`px-2 py-0.5 text-xs rounded ${getNatureColor(itemType.nature)}`}>
              {itemType.nature}
            </span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Costing Method:</span>
            <span className="font-medium">{itemType.costingMethod}</span>
          </div>
          {itemType.description && (
            <div className="text-sm">
              <span className="text-gray-600">Description:</span>
              <p className="text-gray-800 mt-1">{itemType.description}</p>
            </div>
          )}
          
          <div className="flex flex-wrap gap-1 mt-2">
            {itemType.isInventoryTracked && (
              <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                Inventory Tracked
              </span>
            )}
            {itemType.isBatchTracked && (
              <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded">
                Batch Tracked
              </span>
            )}
            {itemType.isSerialTracked && (
              <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded">
                Serial Tracked
              </span>
            )}
            {itemType.hasExpiryDate && (
              <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
                Has Expiry
              </span>
            )}
            {itemType.isMrpApplicable && (
              <span className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-800 rounded">
                MRP
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between pt-3 border-t">
          <button
            onClick={() => toggleStatusMutation.mutate(itemType.id)}
            className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
              itemType.isActive
                ? 'text-green-600 hover:bg-green-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            {itemType.isActive ? (
              <CheckCircleIcon className="h-4 w-4" />
            ) : (
              <XCircleIcon className="h-4 w-4" />
            )}
            {itemType.isActive ? 'Active' : 'Inactive'}
          </button>

          <div className="flex gap-2">
            <button
              onClick={() => openModal(itemType)}
              title="Edit"
              aria-label="Edit"
              className="p-1 hover:bg-gray-100 rounded"
            >
              <PencilIcon className="h-4 w-4 text-gray-600" />
            </button>
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to delete this item type?')) {
                  deleteMutation.mutate(itemType.id);
                }
              }}
              title="Delete"
              aria-label="Delete"
              className="p-1 hover:bg-red-50 rounded"
            >
              <TrashIcon className="h-4 w-4 text-red-600" />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Item Type Management</h1>
        <p className="text-gray-600">Configure item categories and their properties</p>
      </div>

      {/* Filters and Actions */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search item types..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            
            <select
              value={filterNature}
              onChange={(e) => setFilterNature(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Natures</option>
              {Object.entries(ItemNature).map(([key, value]) => (
                <option key={value} value={value}>
                  {key.charAt(0) + key.slice(1).toLowerCase()}
                </option>
              ))}
            </select>

            <select
              value={filterCode}
              onChange={(e) => setFilterCode(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Codes</option>
              {Object.entries(ItemTypeCode).map(([key, value]) => (
                <option key={value} value={value}>
                  {value} - {key.replace(/_/g, ' ')}
                </option>
              ))}
            </select>

            <div className="flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'grid'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Grid
              </button>
              <button
                onClick={() => setViewMode('grouped')}
                className={`px-4 py-2 rounded-lg ${
                  viewMode === 'grouped'
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                Grouped
              </button>
            </div>

            <div></div>

            <Button onClick={() => openModal()} className="flex items-center gap-2">
              <PlusIcon className="h-5 w-5" />
              Add Item Type
            </Button>
          </div>
        </div>
      </Card>

      {/* Content based on view mode */}
      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {itemTypesData?.data?.map((itemType: ItemType) => renderItemTypeCard(itemType))}
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(ItemNature).map(([natureKey, natureValue]) => (
            <Card key={natureValue}>
              <div className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <TagIcon className="h-5 w-5" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {natureKey.charAt(0) + natureKey.slice(1).toLowerCase()}
                  </h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getNatureColor(natureValue)}`}>
                    {groupedData?.[natureValue]?.length || 0} types
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {groupedData?.[natureValue]?.map((itemType: ItemType) => renderItemTypeCard(itemType))}
                </div>
                {(!groupedData?.[natureValue] || groupedData[natureValue].length === 0) && (
                  <p className="text-gray-500 text-center py-4">No item types found for this nature</p>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <ItemTypeModal
          itemType={selectedItemType}
          onClose={closeModal}
          onSubmit={(data) => createUpdateMutation.mutate(data)}
        />
      )}
    </div>
  );
};

// Item Type Modal Component
const ItemTypeModal: React.FC<{
  itemType: ItemType | null;
  onClose: () => void;
  onSubmit: (data: any) => void;
}> = ({ itemType, onClose, onSubmit }) => {
  const { register, handleSubmit, formState: { errors } } = useForm<any>({
    defaultValues: itemType || {
      isInventoryTracked: true,
      isStockValued: true,
      isBatchTracked: false,
      isSerialTracked: false,
      hasExpiryDate: false,
      isMrpApplicable: true,
      isPhantomItem: false,
      allowNegativeStock: true,
      isActive: true,
      costingMethod: CostingMethod.STANDARD,
      nature: ItemNature.PURCHASED,
      code: ItemTypeCode.RAW_MATERIAL,
    },
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4">
          {itemType ? 'Edit Item Type' : 'Add New Item Type'}
        </h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code *
              </label>
              <select
                {...register('code', { required: 'Code is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(ItemTypeCode).map(([key, value]) => (
                  <option key={value} value={value}>
                    {value} - {key.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              {errors.code && (
                <p className="text-red-500 text-sm mt-1">{errors.code.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                {...register('name', { required: 'Name is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-1">{errors.name.message as string}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nature *
              </label>
              <select
                {...register('nature', { required: 'Nature is required' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(ItemNature).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Costing Method
              </label>
              <select
                {...register('costingMethod')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {Object.entries(CostingMethod).map(([key, value]) => (
                  <option key={value} value={value}>
                    {key.charAt(0) + key.slice(1).toLowerCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              {...register('description')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Inventory Control</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isInventoryTracked')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Track Inventory</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isStockValued')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Stock Valued</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isBatchTracked')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Batch Tracked</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isSerialTracked')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Serial Tracked</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('hasExpiryDate')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Has Expiry Date</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('allowNegativeStock')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Allow Negative Stock</span>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Planning & MRP</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isMrpApplicable')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">MRP Applicable</span>
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  {...register('isPhantomItem')}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-700">Phantom Item</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('isActive')}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label className="text-sm text-gray-700">Active</label>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              {itemType ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ItemTypeManagement;